import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@microbeta/ui";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// Icons for different handle types
import { MdDragHandle, MdOutlineZoomOutMap } from "react-icons/md";
import styles from "./ImageCropper.module.scss";

interface ImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (croppedBlob: Blob) => void;
  imageFile: File | null;
  aspectRatio?: number;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

// **FIXED**: Using camelCase to match SCSS classes
type ResizeHandle =
  | "topLeft"
  | "top"
  | "topRight"
  | "left"
  | "right"
  | "bottomLeft"
  | "bottom"
  | "bottomRight";

type Interaction = { type: "drag" | "resize"; handle?: ResizeHandle };

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MIN_CROP_SIZE = 40;

export const ImageCropper: React.FC<ImageCropperProps> = ({
  isOpen,
  onClose,
  onConfirm,
  imageFile,
  aspectRatio = 16 / 9,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.9,
}) => {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<CropArea | null>(null);

  const imageRef = useRef<HTMLImageElement>(null);
  const interactionRef = useRef<{
    startPoint: { x: number; y: number };
    startCrop: { x: number; y: number; width: number; height: number };
    interaction: Interaction;
  } | null>(null);

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageSrc(url);
      setCrop(null);
      return () => URL.revokeObjectURL(url);
    }
    setImageSrc(null);
  }, [imageFile]);

  const initializeCropArea = useCallback(() => {
    if (!imageRef.current) return;
    const { width: displayWidth, height: displayHeight } =
      imageRef.current.getBoundingClientRect();
    const initialCropWidth =
      displayWidth / displayHeight > aspectRatio
        ? displayHeight * 0.85 * aspectRatio
        : displayWidth * 0.85;
    const initialCropHeight = initialCropWidth / aspectRatio;
    setCrop({
      x: (displayWidth - initialCropWidth) / 2,
      y: (displayHeight - initialCropHeight) / 2,
      width: initialCropWidth,
      height: initialCropHeight,
    });
  }, [aspectRatio]);

  const getCoordinates = useCallback(
    (e: MouseEvent | TouchEvent) =>
      "touches" in e
        ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
        : { x: e.clientX, y: e.clientY },
    []
  );

  const onInteractionMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!interactionRef.current || !imageRef.current) return;

      const { startPoint, startCrop, interaction } = interactionRef.current;
      const imageRect = imageRef.current.getBoundingClientRect();
      const currentPoint = getCoordinates(e);
      const deltaX = currentPoint.x - startPoint.x;
      const deltaY = currentPoint.y - startPoint.y;
      let newCrop = { ...startCrop };

      if (interaction.type === "drag") {
        newCrop.x = startCrop.x + deltaX;
        newCrop.y = startCrop.y + deltaY;
      } else if (interaction.type === "resize" && interaction.handle) {
        const handle = interaction.handle;
        let { x, y, width, height } = startCrop;
        if (handle.includes("Left") || handle.includes("Right")) {
          // Corner or L/R edge
          const dx = handle.includes("Left") ? -deltaX : deltaX;
          width += dx;
          const oldHeight = height;
          height = width / aspectRatio;
          if (handle.includes("Left")) x -= dx;
          if (!handle.includes("top") && !handle.includes("bottom"))
            y += (oldHeight - height) / 2;
        }
        if (handle.includes("Top") || handle.includes("Bottom")) {
          // Corner or T/B edge
          const dy = handle.includes("Top") ? -deltaY : deltaY;
          height += dy;
          const oldWidth = width;
          width = height * aspectRatio;
          if (handle.includes("Top")) y -= dy;
          if (!handle.includes("left") && !handle.includes("right"))
            x += (oldWidth - width) / 2;
        }
        newCrop = { x, y, width, height };
      }

      if (newCrop.width > imageRect.width) {
        newCrop.width = imageRect.width;
        newCrop.height = newCrop.width / aspectRatio;
      }
      if (newCrop.height > imageRect.height) {
        newCrop.height = imageRect.height;
        newCrop.width = newCrop.height * aspectRatio;
      }
      if (newCrop.width < MIN_CROP_SIZE) {
        newCrop.width = MIN_CROP_SIZE;
        newCrop.height = newCrop.width / aspectRatio;
      }
      if (newCrop.height < MIN_CROP_SIZE) {
        newCrop.height = MIN_CROP_SIZE;
        newCrop.width = newCrop.height * aspectRatio;
      }
      if (newCrop.x < 0) newCrop.x = 0;
      if (newCrop.y < 0) newCrop.y = 0;
      if (newCrop.x + newCrop.width > imageRect.width)
        newCrop.x = imageRect.width - newCrop.width;
      if (newCrop.y + newCrop.height > imageRect.height)
        newCrop.y = imageRect.height - newCrop.height;

      setCrop(newCrop);
    },
    [aspectRatio, getCoordinates]
  );

  const onInteractionEnd = useCallback(() => {
    interactionRef.current = null;
    document.body.style.cursor = "default";
    window.removeEventListener("mousemove", onInteractionMove);
    window.removeEventListener("touchmove", onInteractionMove);
    window.removeEventListener("mouseup", onInteractionEnd);
    window.removeEventListener("touchend", onInteractionEnd);
  }, [onInteractionMove]);

  const _onInteractionStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent, interaction: Interaction) => {
      e.preventDefault();
      e.stopPropagation();
      if (!crop) return;
      interactionRef.current = {
        startPoint: getCoordinates(e.nativeEvent),
        startCrop: crop,
        interaction,
      };
      if (interaction.type === "resize" && interaction.handle) {
        const handle = interaction.handle;
        if (
          (handle.includes("Top") && handle.includes("Left")) ||
          (handle.includes("Bottom") && handle.includes("Right"))
        )
          document.body.style.cursor = "nwse-resize";
        else if (
          (handle.includes("Top") && handle.includes("Right")) ||
          (handle.includes("Bottom") && handle.includes("Left"))
        )
          document.body.style.cursor = "nesw-resize";
        else if (handle.includes("Top") || handle.includes("Bottom"))
          document.body.style.cursor = "ns-resize";
        else if (handle.includes("Left") || handle.includes("Right"))
          document.body.style.cursor = "ew-resize";
      } else if (interaction.type === "drag") {
        document.body.style.cursor = "grabbing";
      }
      window.addEventListener("mousemove", onInteractionMove);
      window.addEventListener("touchmove", onInteractionMove, {
        passive: false,
      });
      window.addEventListener("mouseup", onInteractionEnd);
      window.addEventListener("touchend", onInteractionEnd);
    },
    [crop, onInteractionMove, onInteractionEnd, getCoordinates]
  );

  const handleConfirm = useCallback(async () => {
    if (!imageRef.current || !crop) return;
    const image = imageRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const sourceX = crop.x * scaleX;
    const sourceY = crop.y * scaleY;
    const sourceWidth = crop.width * scaleX;
    const sourceHeight = crop.height * scaleY;
    let outputWidth = sourceWidth;
    let outputHeight = sourceHeight;
    if (outputWidth > maxWidth) {
      outputWidth = maxWidth;
      outputHeight = outputWidth / aspectRatio;
    }
    if (outputHeight > maxHeight) {
      outputHeight = maxHeight;
      outputWidth = outputHeight * aspectRatio;
    }
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );
    canvas.toBlob(
      (blob) => {
        if (blob) {
          onConfirm(blob);
        } else {
          console.error("Failed to create blob from cropped canvas");
        }
      },
      "image/png",
      quality
    );
  }, [crop, onConfirm, quality, aspectRatio, maxWidth, maxHeight]);

  const cropBoxStyle = useMemo(
    () =>
      crop
        ? {
            transform: `translate(${crop.x}px, ${crop.y}px)`,
            width: `${crop.width}px`,
            height: `${crop.height}px`,
          }
        : { display: "none" },
    [crop]
  );

  // **FIXED**: Using camelCase to match SCSS classes
  const resizeHandles: ResizeHandle[] = [
    "topLeft",
    "top",
    "topRight",
    "left",
    "right",
    "bottomLeft",
    "bottom",
    "bottomRight",
  ];

  const isCornerHandle = (handle: ResizeHandle) =>
    !["top", "bottom", "left", "right"].includes(handle);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={styles.cropDialog}>
        <DialogTitle>{t("imageUploader.cropImageTitle")}</DialogTitle>
        <DialogDescription>
          {t("imageUploader.cropInstructions")}
        </DialogDescription>
        <div className={styles.cropperOuterContainer}>
          <div className={styles.cropperInnerContainer}>
            {imageSrc && (
              <>
                <img
                  ref={imageRef}
                  src={imageSrc}
                  alt={t("imageUploader.cropPreviewAlt")}
                  onLoad={initializeCropArea}
                  className={styles.backgroundImage}
                />
                <div className={styles.cropOverlay}>
                  {crop && (
                    <section
                      className={styles.cropBox}
                      style={cropBoxStyle}
                      onMouseDown={(e) =>
                        _onInteractionStart(e, { type: "drag" })
                      }
                      onTouchStart={(e) =>
                        _onInteractionStart(e, { type: "drag" })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                        }
                      }}
                      aria-label={t("imageUploader.cropBoxArea")}
                    >
                      {resizeHandles.map((handle) => (
                        <button
                          type="button"
                          key={handle}
                          className={`${styles.resizeHandleWrapper} ${styles[handle]}`}
                          aria-label={`Resize handle for ${handle}`}
                          onMouseDown={(e) =>
                            _onInteractionStart(e, { type: "resize", handle })
                          }
                          onTouchStart={(e) =>
                            _onInteractionStart(e, { type: "resize", handle })
                          }
                        >
                          {/* **FIXED**: Robust logic for choosing icon */}
                          {isCornerHandle(handle) ? (
                            <MdOutlineZoomOutMap
                              className={styles.resizeIcon}
                            />
                          ) : (
                            <MdDragHandle className={styles.resizeIconEdge} />
                          )}
                        </button>
                      ))}
                    </section>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose}>
            {t("imageUploader.cancelButton")}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!crop}>
            {t("imageUploader.cropUploadButton")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
